import React, { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import usePaymentProvidersConfig from './hooks/usePaymentProvidersConfig';
import type { PaymentProviderName } from 'domain/paySettings/helpers';

import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Provider from './components/Provider';
import DialogPaymentProviderDetails from './components/DialogPaymentProviderDetails';

const Providers: React.FC = () => {
  const { formatMessage } = useIntl();
  const paymentProvidersConfig = usePaymentProvidersConfig();
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  const { providerTitle, providerName, isAvailableWallets } = useMemo(() => {
    if (selectedProviderId) {
      const foundProvider = paymentProvidersConfig.find((item) => item.id === selectedProviderId);
      return {
        providerTitle: foundProvider?.title || '',
        providerName: foundProvider?.name || '',
        isAvailableWallets: foundProvider?.isAvailableWallets ?? true,
      };
    }
    return { providerTitle: '', providerName: '', isAvailableWallets: true };
  }, [selectedProviderId, paymentProvidersConfig]);

  const closeDetailsDialog = useCallback(() => {
    setSelectedProviderId(null);
  }, [setSelectedProviderId]);

  return (
    <Stack component={Paper} variant="outlined" p={2}>
      <Typography variant="subtitle1" fontWeight={500}>
        {formatMessage({
          id: 'configurations.company.paymentProviders.title',
          defaultMessage: 'Payment Providers',
        })}
      </Typography>
      {paymentProvidersConfig.map(({ title, name, ...rest }) => (
        <Provider
          key={title}
          title={title}
          name={name as PaymentProviderName}
          {...rest}
          setSelectedProviderId={setSelectedProviderId}
        />
      ))}
      {!!selectedProviderId && (
        <DialogPaymentProviderDetails
          id={selectedProviderId}
          onClose={closeDetailsDialog}
          title={providerTitle}
          providerName={providerName}
          isAvailableWallets={isAvailableWallets}
        />
      )}
    </Stack>
  );
};

export default Providers;
